import { Archive, MoreHorizontal, Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { trpc } from '~/api/trpc';
import { BillingDraftsTable } from '~/components/billing-drafts/BillingDraftsTable';
import { CancellationFeeTierDialog, type CancellationFeeTierRow } from '~/components/cancellation-fees/CancellationFeeTierDialog';
import { CreateCancellationFeeDialog, type CreateCancellationFeePrefill } from '~/components/cancellation-fees/CreateCancellationFeeDialog';
import { PendingCancellationFeesTable } from '~/components/cancellation-fees/PendingCancellationFeesTable';
import { TodaysCancelledAppointmentsTable } from '~/components/cancellation-fees/TodaysCancelledAppointmentsTable';
import { cancellationFeeTypeLabels, formatDurationRange, formatEur } from '~/lib/cancellation-fee-config';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';
import Content from '../components/layout/Content';

const CancellationFeeTiersTab = ({ isAdmin }: { isAdmin: boolean }) => {
    const trpcUtils = trpc.useUtils();
    const [tierDialogOpen, setTierDialogOpen] = useState(false);
    const [editingTier, setEditingTier] = useState<CancellationFeeTierRow | null>(null);

    const { data: tiers, isLoading } = trpc.cancellationFees.getTiers.useQuery({ includeInactive: true });

    const archiveMutation = trpc.cancellationFees.archiveTier.useMutation({
        onSuccess: () => {
            toast.success('Gebührenstufe archiviert');
            trpcUtils.cancellationFees.getTiers.invalidate();
            trpcUtils.cancellationFees.getTiersForAppointment.invalidate();
        },
        onError: (error) => toast.error(error.message),
    });

    const openTierDialog = (tier: CancellationFeeTierRow | null) => {
        setEditingTier(tier);
        setTierDialogOpen(true);
    };

    return (
        <>
            {isAdmin && (
                <div className='flex justify-end mb-4'>
                    <Button variant='outline' onClick={() => openTierDialog(null)}>
                        <Plus className='mr-2 h-4 w-4' />
                        Neue Gebührenstufe
                    </Button>
                </div>
            )}

            <Card>
                <CardContent className='p-0'>
                    {isLoading ? (
                        <div className='p-6 space-y-4'>
                            {[...Array(3)].map((_, i) => (
                                <Skeleton key={i} className='h-12 w-full' />
                            ))}
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Typ</TableHead>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Filiale</TableHead>
                                    <TableHead>Termindauer</TableHead>
                                    <TableHead>Betrag (brutto)</TableHead>
                                    <TableHead>MwSt.</TableHead>
                                    <TableHead>Status</TableHead>
                                    {isAdmin && <TableHead />}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {tiers && tiers.length > 0 ? (
                                    tiers.map((tier) => (
                                        <TableRow key={tier.id}>
                                            <TableCell>
                                                <Badge variant={tier.feeType === 'NO_SHOW' ? 'destructive' : 'secondary'}>
                                                    {cancellationFeeTypeLabels[tier.feeType]}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className='font-medium'>{tier.name}</TableCell>
                                            <TableCell>{tier.store?.name ?? 'Alle Filialen'}</TableCell>
                                            <TableCell>{formatDurationRange(tier.minDurationMinutes, tier.maxDurationMinutes)}</TableCell>
                                            <TableCell>{formatEur(tier.unitPriceGross)}</TableCell>
                                            <TableCell>{(tier.vatRate * 100).toLocaleString('de-DE')} %</TableCell>
                                            <TableCell>
                                                <Badge variant={tier.isActive ? 'default' : 'outline'}>
                                                    {tier.isActive ? 'Aktiv' : 'Inaktiv'}
                                                </Badge>
                                            </TableCell>
                                            {isAdmin && (
                                                <TableCell>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant='ghost' className='h-8 w-8 p-0'>
                                                                <span className='sr-only'>Menü öffnen</span>
                                                                <MoreHorizontal className='h-4 w-4' />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align='end'>
                                                            <DropdownMenuItem onClick={() => openTierDialog(tier)}>
                                                                <Pencil className='mr-2 h-4 w-4' />
                                                                Bearbeiten
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem
                                                                onClick={() => {
                                                                    if (confirm(`Gebührenstufe "${tier.name}" archivieren?`)) {
                                                                        archiveMutation.mutate({ id: tier.id });
                                                                    }
                                                                }}
                                                            >
                                                                <Archive className='mr-2 h-4 w-4' />
                                                                Archivieren
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={isAdmin ? 8 : 7} className='h-24 text-center'>
                                            Keine Gebührenstufen angelegt.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {isAdmin && (
                <CancellationFeeTierDialog
                    open={tierDialogOpen}
                    onOpenChange={setTierDialogOpen}
                    tier={editingTier}
                />
            )}
        </>
    );
};

const CancellationFees = () => {
    const { role } = useUserStore(
        useShallow((state) => ({ role: state.user?.role }))
    );
    const isAdmin = role === UserRole.ADMIN;
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [createPrefill, setCreatePrefill] = useState<CreateCancellationFeePrefill | null>(null);

    const openCreateDialog = (prefill: CreateCancellationFeePrefill | null) => {
        setCreatePrefill(prefill);
        setCreateDialogOpen(true);
    };

    return (
        <Content>
            <div className='container mx-auto py-10'>
                <div className='flex justify-between items-center mb-6'>
                    <h1 className='text-2xl font-bold'>Ausfallentschädigung</h1>
                    <Button onClick={() => openCreateDialog(null)}>
                        <Plus className='mr-2 h-4 w-4' />
                        Neue Ausfallentschädigung
                    </Button>
                </div>

                <Tabs defaultValue='drafts'>
                    <TabsList className='mb-6'>
                        <TabsTrigger value='drafts'>Ausfallentschädigungen</TabsTrigger>
                        <TabsTrigger value='pending'>Geplante Ausfallentschädigungen</TabsTrigger>
                        <TabsTrigger value='today'>Absagen & No-Shows (3 Tage)</TabsTrigger>
                        <TabsTrigger value='tiers'>Gebührenstufen</TabsTrigger>
                    </TabsList>
                    <TabsContent value='drafts'>
                        <BillingDraftsTable fixedFilters={{ hasCancellationFee: true }} />
                    </TabsContent>
                    <TabsContent value='pending'>
                        <PendingCancellationFeesTable />
                    </TabsContent>
                    <TabsContent value='today'>
                        <TodaysCancelledAppointmentsTable onCreate={openCreateDialog} />
                    </TabsContent>
                    <TabsContent value='tiers'>
                        <CancellationFeeTiersTab isAdmin={isAdmin} />
                    </TabsContent>
                </Tabs>
            </div>

            <CreateCancellationFeeDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} prefill={createPrefill} />
        </Content>
    );
};

export default CancellationFees;
